import { BaseInput } from '@/components/Form/BaseInput';
import { InformationCircleIcon } from '@heroicons/react/16/solid';
import React, { forwardRef } from 'react';
import { tv, VariantProps } from 'tailwind-variants';
import { FieldWrapper } from './FieldWrapper';

const style = tv({
  variants: {
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '3xl': 'rounded-3xl',
    },
    error: {
      true: 'border-red-600',
    },
    disabled: {
      true: 'bg-gray-100',
    },
  },
  defaultVariants: {
    rounded: 'md',
  },
});

type InputFieldProps = Omit<React.ComponentProps<typeof BaseInput>, 'label'> &
  VariantProps<typeof style> & {
    label?: React.ReactNode;
    errors?: string[];
  };

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    { type = 'text', label, rounded, className, icon, error, errors, ...props },
    ref
  ) => {
    return (
      <FieldWrapper label={label}>
        <BaseInput
          ref={ref}
          type={type}
          className={style({
            error,
            rounded,
            className,
            disabled: props.disabled,
          })}
          icon={icon}
          {...props}
        />

        {error && errors && (
          <div className="mt-1 flex-col items-center text-sm font-normal text-red-600">
            {errors.map((message: string) => (
              <div key={message} className="flex">
                <InformationCircleIcon className="mr-2 mt-0.5 size-4 shrink-0" />
                {message}
              </div>
            ))}
          </div>
        )}
      </FieldWrapper>
    );
  }
);

InputField.displayName = 'InputField';
